import React from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import moment from 'moment';
import Pagination from 'components/v2/PaginationV2';
import _ from 'underscore';
import Container from 'components/v2/container';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import { humanize } from 'underscore.string';
import { withTranslation, Trans } from 'react-i18next';
import queryObject from 'utils/query-object';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import DateRangeFilter from 'components/time/DateRangeFilter';
import CargomaticInput from 'components/v2/input';
import Badge from 'components/v2/Badge';
import StatusFilter from 'components/ui/filters/status-filter';
import formatMoney from 'utils/formatters/Money';
import { getDefaultDateRangeFilter } from 'helpers/date-range';
import CargomaticSelect from 'components/v2/select';

import { exportCarrierPayouts } from 'utils/AccountingWebAPIUtils';
import PayoutList from './payout-list';
import SessionStore from '../../../../../stores/SessionStore';

function getTrueKeys(obj) {
  const keys = [];
  _.map(obj, (v, k) => {
    if (v) {
      keys.push(k);
    }
  });
  return keys;
}

const DEFAULT_DATE_RANGE_VALUES = {
  startValue: 30,
  startTimeUnit: 'd',
  endValue: 0,
  endTimeUnit: 'd',
  startKey: {
    key: 'date_created',
  },
};

class CarrierPayoutModule extends React.Component {
  constructor(props) {
    super(props);
    this.getContexts();
    const initial = {
      filters: {
        page: 1,
        page_size: 20,
        sort: {
          key: 'due_date',
          order: 'desc',
        },
        date_range: getDefaultDateRangeFilter(DEFAULT_DATE_RANGE_VALUES),
        shipment: [],
        number: [],
        status: {
          internal_review: true,
          payment_initialized: true,
          paid: true,
          approved_internally: true,
          void: false,
        },
      },
      filteredPayouts: [],
      payouts: [],
      shipmentSearch: '',
      payoutSearch: '',
      selectedCarrier: null,
    };

    initial.filters = Object.assign(initial.filters, this.getUrlParams());

    this.state = initial;
  }

  componentDidMount() {
    this.queryData(this.state.filters);
  }

  async getContexts() {
    const user = SessionStore.getUser();
    const carrierScope = { _id: user._id, name: user.name };
    const scopes = await UserWebAPIUtils.getUserContextGrants(['payouts']);
    if (scopes) {
      const payoutScopes = scopes.payouts;

      if (payoutScopes) {
        this.setState({
          accounts: [...payoutScopes.accounts, carrierScope],
          hasMultiAccountAccess: payoutScopes.hasMultiAccountAccess,
        });
      }
    }
  }

  onSortChange() {
    return (sort) => {
      this.queryData(this.extendFilters({ sort }));
    };
  }

  onPaginationSelect(eventKey) {
    this.queryData(this.extendFilters({ page: eventKey }));
  }

  onDateRangeChange = ({ filterType, start, end }) => {
    this.setState(
      (prevState) => {
        const filters = prevState.filters;
        if (filterType === 'clear') {
          filters.date_range = getDefaultDateRangeFilter(
            DEFAULT_DATE_RANGE_VALUES,
          );
        } else {
          filters.sort.key = filterType;
          filters.date_range = {
            key: filterType,
            start: moment(start).toISOString(),
            end: moment(end).toISOString(),
            page: 1,
          };
        }
        return { filters };
      },
      () => {
        this.queryData(this.state.filters);
      },
    );
  };

  getUrlParams() {
    // Adding this so page redirects don't get stuck in an infinite loop
    if (
      this.props.location.pathname &&
      this.props.location.pathname.indexOf('carrier-payouts') === -1
    ) {
      return null;
    }

    const rawParams = queryObject(this.props.location);
    const params = {};

    // unfortunately qs doesn't convert strings to booleans, so we'll have to do that ourselves

    const booleanFilters = ['status', 'transfer_status', 'auto_pay'];
    const arrayFilters = ['shipment', 'number'];

    Object.keys(rawParams).forEach((param) => {
      const key = decodeURIComponent(param);
      const val = decodeURIComponent(rawParams[param]);
      if (!/\[/.test(key)) {
        params[key] = val;
      } else {
        const keyElements = key.split('[');
        const topKey = keyElements[0];
        const subKey = keyElements[1].replace(']', '');
        if (arrayFilters.includes(topKey)) {
          if (!params[topKey]) {
            params[topKey] = [val];
          } else {
            params[topKey] = [...params[topKey], val];
          }
        } else {
          if (!params[topKey]) {
            params[topKey] = {};
          }
          // unfortunately qs doesn't convert strings to booleans, so we'll have to do that ourselves
          if (booleanFilters.indexOf(topKey) > -1) {
            params[topKey][subKey] = val === 'true';
          } else {
            params[topKey][subKey] = val;
          }
        }
      }
    });

    return params;
  }

  getDateRangeFilter() {
    const dateRange = this.state.filters.date_range;
    const defaultDateRangeFilter = getDefaultDateRangeFilter({
      options: 'object',
      ...DEFAULT_DATE_RANGE_VALUES,
    });

    return (
      <DateRangeFilter
        className="dropdown-filter-wrapper"
        onChange={this.onDateRangeChange}
        initialStart={defaultDateRangeFilter.start}
        initialEnd={defaultDateRangeFilter.end}
        startKey={dateRange.key || 'due_date'}
        endKey={dateRange.key === 'date_created' ? 'due_date' : 'date_created'}
      />
    );
  }

  generateStatusFilter(title, filter, clearable, handler) {
    const statuses = this.state.filters[filter];
    return (
      <StatusFilter
        clear={clearable}
        className="dropdown-filter-wrapper"
        title={title}
        actives={getTrueKeys(statuses)}
        items={Object.keys(statuses)}
        onChange={handler}
      />
    );
  }

  handleCarrierFilterSelect = (item) => {
    if (!item) {
      this.queryData(this.state.filters);
    } else {
      this.setState({ selectedCarrier: item.name }, () => {
        this.queryData(this.extendFilters({ carrier: item._id }));
      });
    }
  };

  getCarrierFilter = () => {
    const carrierFilterOptions = this.state.accounts.map((a, index) => {
      return {
        ...a,
        key: index,
        value: a.name,
      };
    });

    return (
      <div>
        <CargomaticSelect
          showCaption
          selected={this.state.selectedCarrier}
          caption="Carrier Select"
          className="w-28"
          items={carrierFilterOptions}
          onSelect={this.handleCarrierFilterSelect}
          applyDefaultMargins={false}
          valueKey="_id"
          textKey="name"
          hideLabel
        />
      </div>
    );
  };

  queryData(filters) {
    const id = SessionStore.getUser()._id;

    AccountingActionCreators.getCarrierPayouts(id, filters).then((results) => {
      // TO:DO Remove this after we have a better solution
      /* eslint-disable  */

      const payouts = results.entity.data.map((payout) => {
        const lineItemsData = payout.lineItemGroups.reduce(
          (acc, cur) => {
            acc.shipmentIds.push(cur.shipmentId);
            acc.items = _.uniq([...acc.items, ...cur.items]);
            return acc;
          },
          { shipmentIds: [], items: [] },
        );
        return { ...payout, ...lineItemsData };
      });
      const {
        totalRevenue,
        count,
        totalPaid,
        totalPaidCount,
        totalPaymentInitialized,
        totalPaymentInitializedCount,
        totalInternalReview,
        totalInternalReviewCount,
      } = results.entity.meta;

      const totalPages = Math.ceil(count / this.state.filters.page_size);
      this.updateUrl(filters);
      this.setState({
        count,
        totalPaid,
        totalPaidCount,
        totalPaymentInitialized,
        totalPaymentInitializedCount,
        totalInternalReview,
        totalInternalReviewCount,
        filters,
        payouts,
        totalPages,
        totalRevenue,
      });
      /* eslint-enable  */
    });
  }

  updateUrl(filters) {
    const currentPath = this.props.location.pathname;
    this.props.history.push(`${currentPath}?${qs.stringify(filters)}`);
  }

  extendFilters(filter) {
    return Object.assign({}, this.state.filters, filter);
  }

  resetFilter(filter) {
    if (!this.state.filters[filter]) {
      return null;
    }

    return (actives) => {
      const statuses = Object.assign({}, this.state.filters[filter]);
      _.each(statuses, (v, status) => {
        statuses[status] = actives.indexOf(status) >= 0;
      });

      this.queryData(
        this.extendFilters({
          [filter]: Object.assign({}, this.state.filters[filter], statuses),
        }),
      );
    };
  }

  renderActiveFilters(filters) {
    const { t } = this.props;
    const humanizeKeyList = (keys) =>
      keys.map((key) => `${t(key.toUpperCase())}`).join(', ');

    const msg = [''];
    let key = 1;

    if (filters.date_range.key) {
      const text = t('FOR_DUE_DATE_FROM', {
        key: t(humanize(filters.date_range.key).toUpperCase()).toLowerCase(),
        startDate: moment(filters.date_range.start).format('L'),
        endDate: moment(filters.date_range.end).format('L'),
      });

      msg.push(
        <div>
          <span key={key} className="font-bold">
            {t('FILTERING_PAYOUTS')}:
          </span>
          <span> {text}&nbsp;</span>
        </div>,
      );
      key += 1;
    }

    const trueStatusKeys = getTrueKeys(filters.status);

    if (trueStatusKeys.length > 0) {
      msg.push(
        <span key={key}>
          {t('WITH_STATUS_OF', {
            statusString: humanizeKeyList(trueStatusKeys),
          })}
        </span>,
      );
      key += 1;
    }

    const trueTransferStatusKeys = getTrueKeys(filters.transfer_status);

    if (trueTransferStatusKeys.length > 0) {
      msg.push(
        <span key={key}>
          {t('WITH_TRANSFER_STATUS_OF', {
            statusString: humanizeKeyList(trueTransferStatusKeys),
          })}
        </span>,
      );
      key += 1;
    }

    msg.push(
      <span key={key}>
        &nbsp;
        {t('WITH_ID', {
          withID: this.state.filters.shipment.map((s) => ` ${s}`),
        })}
      </span>,
    );

    key += 1;

    return (
      <div className="md:w-1/2 w-full">
        <p>{msg}</p>
      </div>
    );
  }

  renderHeader() {
    const { t } = this.props;

    const dateRangeFilter = this.getDateRangeFilter();

    const statusFilters = this.generateStatusFilter(
      t('PAYOUT_STATUS'),
      'status',
      true,
      this.resetFilter('status'),
    );

    return (
      <div className="flex justify-between my-8">
        <div className="flex">
          {this.state.hasMultiAccountAccess && (
            <span className="mb-auto mr-8">{this.getCarrierFilter()}</span>
          )}
          <span className="ml-12 mb-auto">{dateRangeFilter}</span>
          <span className="ml-12 -mt-2 w-52">
            {this.renderMultiShipmentSearch()}
          </span>
          <span className="ml-12 -mt-2 w-52">
            {this.renderMultiPayoutSearch()}
          </span>
          <div className="md:px-4">{statusFilters}</div>
        </div>
        <div className="lead text-right">
          <Trans
            i18nKey="INTERNAL_REVIEW_TOTALLING"
            values={{
              internal_review_count: this.state.totalInternalReviewCount,
              internal_review_amount: formatMoney(
                this.state.totalInternalReview,
                DEFAULT_CURRENCY,
              ),
            }}
            components={[<strong />, <strong />]}
          />
          <br />
          <Trans
            i18nKey="PAYMENT_INITIALIZED_TOTALLING"
            values={{
              payment_initialized_count:
                this.state.totalPaymentInitializedCount,
              payment_initialized_amount: formatMoney(
                this.state.totalPaymentInitialized,
                DEFAULT_CURRENCY,
              ),
            }}
            components={[<strong />, <strong />]}
          />
          <br />
          <Trans
            i18nKey="PAID_TOTALLING"
            values={{
              total_paid_count: this.state.totalPaidCount,
              total_paid_amount: formatMoney(
                this.state.totalPaid,
                DEFAULT_CURRENCY,
              ),
            }}
            components={[<strong />, <strong />]}
          />
          <br />
        </div>
      </div>
    );
  }

  renderMultiPayoutSearch() {
    const { t } = this.props;

    return (
      <>
        <CargomaticInput
          name="number"
          value={this.state.payoutSearch}
          onChange={(value) => this.handlePayoutSearchChange(value)}
          onKeyDown={(key) => this.handlePayoutSearch(key)}
          type="text"
          placeholder={`${t('SEARCH_BY_PAYOUT_NUMBER')}`}
        />

        <div className="flex flex-wrap items-center justify-start mt-2">
          {this.state.filters.number.map((s) => {
            return (
              <div className="mt-2 mr-2" key={s}>
                <Badge
                  color="green"
                  withRemove
                  onRemove={() => this.handlePayoutFilterRemove(s)}
                >
                  {s}
                </Badge>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  renderMultiShipmentSearch() {
    const { t } = this.props;

    return (
      <>
        <CargomaticInput
          name="shipment"
          value={this.state.shipmentSearch}
          onChange={(value) => this.handleShipmentSearchChange(value)}
          onKeyDown={(key) => this.handleShipmentSearch(key)}
          type="text"
          placeholder={`${t('SEARCH_BY_SHIPMENT_ID')}`}
        />

        <div className="flex flex-wrap items-center justify-start mt-2">
          {this.state.filters.shipment.map((s) => {
            return (
              <div className="mt-2 mr-2" key={s}>
                <Badge
                  color="green"
                  withRemove
                  onRemove={() => this.handleShipmentFilterRemove(s)}
                >
                  {s}
                </Badge>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  handlePayoutSearchChange(value) {
    this.setState({ payoutSearch: value });
  }

  handleShipmentSearchChange(value) {
    this.setState({ shipmentSearch: value });
  }

  handleShipmentSearch(e) {
    if (e.keyCode === 13) {
      this.setState(
        (prevState) => {
          const shipment = prevState.filters.shipment.concat([
            prevState.shipmentSearch.toUpperCase(),
          ]);
          const filters = Object.assign({}, prevState.filters, {
            shipment,
          });
          return {
            filters,
            shipmentSearch: '',
          };
        },
        () => {
          this.queryData(
            this.extendFilters({
              shipment: this.state.filters.shipment,
              page: 1,
            }),
          );
        },
      );
    }
  }

  handlePayoutSearch(e) {
    if (e.keyCode === 13) {
      this.setState(
        (prevState) => {
          const number = prevState.filters.number.concat([
            prevState.payoutSearch.toUpperCase(),
          ]);
          const filters = Object.assign({}, prevState.filters, {
            number,
          });
          return {
            filters,
            payoutSearch: '',
          };
        },
        () => {
          this.queryData(
            this.extendFilters({
              number: this.state.filters.number,
              page: 1,
            }),
          );
        },
      );
    }
  }

  handleShipmentFilterRemove(item) {
    this.setState(
      (prevState) => {
        const filteredShipments = prevState.filters.shipment.filter(
          (shipment) => shipment !== item,
        );
        return {
          ...prevState,
          filters: { ...prevState.filters, shipment: filteredShipments },
        };
      },
      () => {
        this.queryData(
          this.extendFilters({
            shipment: this.state.filters.shipment,
            page: 1,
          }),
        );
      },
    );
  }

  handlePayoutFilterRemove(item) {
    this.setState(
      (prevState) => {
        const filteredPayouts = prevState.filters.number.filter(
          (payoutNumber) => payoutNumber !== item,
        );
        return {
          ...prevState,
          filters: { ...prevState.filters, number: filteredPayouts },
        };
      },
      () => {
        this.queryData(
          this.extendFilters({
            number: this.state.filters.number,
            page: 1,
          }),
        );
      },
    );
  }

  async handleDownloadCSVReport(filters) {
    try {
      const id = SessionStore.getUser()._id;
      await exportCarrierPayouts(id, filters);
    } catch (e) {
      // Handled in APIUtils
    }
  }

  render() {
    const { filteredPayouts, payouts, filters } = this.state;
    const payoutData = filteredPayouts.length > 0 ? filteredPayouts : payouts;

    return (
      <Container className="py-8">
        {this.renderHeader()}
        {this.renderActiveFilters(this.state.filters)}
        <div className="mt-4">
          <PayoutList
            payouts={payoutData}
            sort={this.state.filters.sort}
            onSortChange={this.onSortChange()}
            handleDownloadCSVReport={() =>
              this.handleDownloadCSVReport(filters)
            }
          />

          <Pagination
            first
            last
            pages={this.state.totalPages}
            page={parseInt(this.state.filters.page, 10)}
            onPageClick={(eventKey) => this.onPaginationSelect(eventKey)}
            perPage={this.props.perPage}
            total={this.state.count}
          />
        </div>
      </Container>
    );
  }
}

CarrierPayoutModule.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withTranslation()(CarrierPayoutModule);
